import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { toast } from "sonner";
import { Plus, X, Check, Clock, AlertCircle, FileText, Edit3, Trash2, ChevronDown } from "lucide-react";

const TYPE_META = {
  promise:   { label: "Promise",   color: "#5A90F5" },
  contract:  { label: "Contract",  color: "#A855F7" },
  policy:    { label: "Policy",    color: "#14B8A6" },
  agreement: { label: "Agreement", color: "#FF8A00" },
};
const STATUS_META = {
  active:       { label: "Active",       color: "#5A90F5", bg: "#5A90F520" },
  kept:         { label: "Kept",         color: "#00C781", bg: "#00C78120" },
  broken:       { label: "Broken",       color: "#EF4444", bg: "#EF444420" },
  renegotiated: { label: "Renegotiated", color: "#F59E0B", bg: "#F59E0B20" },
};

function CommitmentForm({ initial, onClose, onSave }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [desc, setDesc] = useState(initial?.description || "");
  const [type, setType] = useState(initial?.type || "promise");
  const [parties, setParties] = useState((initial?.parties || []).join(", "));
  const [dueDate, setDueDate] = useState(initial?.due_date?.slice(0,10) || "");
  return (
    <div className="fixed inset-0 z-[500] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#111] border border-[#2A2A2A] rounded-2xl p-6 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
        <h3 className="text-[15px] font-semibold text-[#EDEDED] mb-4">{initial ? "Edit" : "New"} commitment</h3>
        <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Commitment title..."
          className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl px-3 py-2.5 text-[13px] text-[#EDEDED] placeholder-[#3A3A3A] outline-none focus:border-[#5A90F5] mb-3" />
        <textarea value={desc} onChange={e => setDesc(e.target.value)} placeholder="Description..."
          className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl px-3 py-2.5 text-[12px] text-[#A1A1AA] placeholder-[#3A3A3A] outline-none resize-none focus:border-[#5A90F5] mb-3" rows={2} />
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-[10px] text-[#52525B] mb-1 block">Type</label>
            <select value={type} onChange={e => setType(e.target.value)}
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl px-3 py-2 text-[12px] text-[#EDEDED] outline-none">
              {Object.entries(TYPE_META).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-[#52525B] mb-1 block">Due date</label>
            <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl px-3 py-2 text-[12px] text-[#EDEDED] outline-none" />
          </div>
        </div>
        <input value={parties} onChange={e => setParties(e.target.value)} placeholder="Parties involved (comma separated)..."
          className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl px-3 py-2.5 text-[12px] text-[#EDEDED] placeholder-[#3A3A3A] outline-none focus:border-[#5A90F5] mb-4" />
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2 rounded-xl text-[12px] border border-[#2A2A2A] text-[#52525B]">Cancel</button>
          <button onClick={() => { if(title.trim()) onSave({ title:title.trim(), description:desc, type, parties:parties.split(",").map(s=>s.trim()).filter(Boolean), due_date:dueDate||null }); }}
            disabled={!title.trim()} className="flex-1 py-2 rounded-xl text-[12px] font-semibold bg-[#5A90F5] text-white disabled:opacity-30">Save</button>
        </div>
      </div>
    </div>
  );
}

export default function Commitments() {
  const { current } = useWorkspace();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("active");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const wsId = current?.workspace_id;

  const load = useCallback(async () => {
    if (!wsId) return;
    try { const { data } = await api.get(`/workspaces/${wsId}/commitments`, { params: { status: filter === "all" ? undefined : filter } }); setItems(data); } catch {}
  }, [wsId, filter]);

  useEffect(() => { load(); }, [load]);

  const create = async (data) => {
    try { const { data: c } = await api.post(`/workspaces/${wsId}/commitments`, data); setItems(i => [c, ...i]); setShowForm(false); } catch { toast.error("Failed"); }
  };
  const update = async (data) => {
    try { const { data: c } = await api.put(`/workspaces/${wsId}/commitments/${editing.commitment_id}`, data); setItems(i => i.map(x => x.commitment_id===c.commitment_id?c:x)); setEditing(null); } catch {}
  };
  const setStatus = async (id, status) => {
    try { const { data: c } = await api.put(`/workspaces/${wsId}/commitments/${id}`, { status }); setItems(i => i.map(x => x.commitment_id===id?c:x)); } catch {}
  };
  const del = async (id) => {
    if(!window.confirm("Delete this commitment?")) return;
    try { await api.delete(`/workspaces/${wsId}/commitments/${id}`); setItems(i => i.filter(x=>x.commitment_id!==id)); } catch {}
  };

  return (
    <div className="flex flex-col h-full bg-[#0A0A0A]">
      <div className="px-8 py-5 border-b border-[#1A1A1A] flex items-center justify-between">
        <div>
          <h1 className="text-[18px] font-bold text-[#EDEDED]">Commitments</h1>
          <p className="text-[11px] text-[#3A3A3A] mt-0.5">{items.filter(i=>i.status==="active").length} active</p>
        </div>
        <button onClick={() => setShowForm(true)} className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#5A90F5] hover:bg-[#4A80E5] text-white text-[12px] font-semibold transition-colors">
          <Plus className="w-3.5 h-3.5" /> New
        </button>
      </div>
      {/* Filter tabs */}
      <div className="px-8 py-3 border-b border-[#1A1A1A] flex gap-2">
        {["active","kept","broken","renegotiated","all"].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1 rounded-lg text-[10px] capitalize border transition-colors ${filter===f?"border-[#5A90F5]/40 bg-[#5A90F5]/10 text-[#5A90F5]":"border-[#2A2A2A] text-[#3A3A3A] hover:text-[#52525B]"}`}>
            {f}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto px-8 py-4 space-y-2">
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Check className="w-8 h-8 text-[#2A2A2A]" />
            <p className="text-[13px] text-[#3A3A3A]">No {filter === "all" ? "" : filter} commitments</p>
          </div>
        ) : items.map(c => {
          const tm = TYPE_META[c.type] || TYPE_META.promise;
          const sm = STATUS_META[c.status] || STATUS_META.active;
          const overdue = c.due_date && new Date(c.due_date) < new Date() && c.status === "active";
          return (
            <div key={c.commitment_id} className="group flex items-start gap-4 p-4 rounded-xl border border-[#2A2A2A] bg-[#111] hover:border-[#3A3A3A] transition-all">
              <div className="w-1 rounded-full flex-shrink-0 self-stretch" style={{ background: tm.color }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[13px] font-medium text-[#EDEDED] truncate">{c.title}</p>
                    {c.description && <p className="text-[11px] text-[#52525B] mt-0.5 line-clamp-1">{c.description}</p>}
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className="text-[9px] px-2 py-0.5 rounded-full font-medium" style={{ background: sm.bg, color: sm.color }}>{sm.label}</span>
                    <span className="text-[9px] px-2 py-0.5 rounded-full bg-[#1A1A1A] text-[#52525B]">{tm.label}</span>
                  </div>
                </div>
                <div className="flex items-center gap-4 mt-2">
                  {c.parties?.length > 0 && <span className="text-[10px] text-[#52525B]">with {c.parties.slice(0,2).join(", ")}{c.parties.length>2?` +${c.parties.length-2}`:""}</span>}
                  {c.due_date && <span className={`text-[10px] flex items-center gap-1 ${overdue?"text-[#EF4444]":"text-[#52525B]"}`}><Clock className="w-3 h-3"/>{new Date(c.due_date).toLocaleDateString([],{month:"short",day:"numeric"})}{overdue?" — overdue":""}</span>}
                </div>
                {c.status === "active" && (
                  <div className="flex gap-2 mt-3 opacity-0 group-hover:opacity-100 transition-all">
                    <button onClick={() => setStatus(c.commitment_id,"kept")} className="px-2.5 py-1 rounded-lg text-[10px] bg-[#00C781]/10 text-[#00C781] border border-[#00C781]/20 hover:bg-[#00C781]/20">Mark kept</button>
                    <button onClick={() => setStatus(c.commitment_id,"broken")} className="px-2.5 py-1 rounded-lg text-[10px] bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/20 hover:bg-[#EF4444]/20">Mark broken</button>
                    <button onClick={() => setStatus(c.commitment_id,"renegotiated")} className="px-2.5 py-1 rounded-lg text-[10px] bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/20">Renegotiate</button>
                    <button onClick={() => setEditing(c)} className="p-1.5 rounded-lg hover:bg-[#1a1a1a] text-[#3A3A3A] hover:text-[#A1A1AA] ml-auto"><Edit3 className="w-3.5 h-3.5"/></button>
                    <button onClick={() => del(c.commitment_id)} className="p-1.5 rounded-lg hover:bg-[#EF4444]/10 text-[#3A3A3A] hover:text-[#EF4444]"><Trash2 className="w-3.5 h-3.5"/></button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {showForm && <CommitmentForm onClose={() => setShowForm(false)} onSave={create} />}
      {editing && <CommitmentForm initial={editing} onClose={() => setEditing(null)} onSave={update} />}
    </div>
  );
}
